import { adapter } from '@seek/tealium-adapter';

export interface AnalyticsEvent {
  event: string;
  attributes: Record<string, unknown>;
}

export interface Analytics<
  LinkEvent extends AnalyticsEvent,
  ViewEvent extends AnalyticsEvent,
> {
  trackLink: (event: LinkEvent) => void;
  trackView: (event: ViewEvent) => void;
  adapter: {
    link: (event: AnalyticsEvent) => void;
    view: (event: AnalyticsEvent) => void;
  };
}

interface CreateTealiumAdapterParams {
  debug?: boolean;
  baseAttributes?: Record<string, unknown>;
}

/**
 * This create the tealium adapter with types configured and also export raw adapter for case that without the types
 *
 * @template LinkEvent - The type of analytics link events.
 * @template ViewEvent - The type of analytics view events.
 *
 * @param {Object} [params={}] - Configuration parameters for the adapter.
 * @param {boolean} [params.debug=false] - Whether to enable debug mode for the adapter.
 * @param {Object} [params.baseAttributes={}] - The base attributes to include with every event.
 *
 * @returns {Analytics<LinkEvent, ViewEvent>} An analytics adapter configured for Tealium.
 */
export const createTealiumAdapter = <
  LinkEvent extends AnalyticsEvent,
  ViewEvent extends AnalyticsEvent,
>({
  debug = false,
  baseAttributes = {},
}: CreateTealiumAdapterParams = {}): Analytics<LinkEvent, ViewEvent> => {
  if (debug) {
    (window as any).ANALYTICS_DEBUG = true;
  }

  const tealiumAdapter = adapter();
  return {
    trackLink: ({ event, attributes }) =>
      tealiumAdapter.link(event, { ...baseAttributes, ...attributes }),
    trackView: ({ event, attributes }) =>
      tealiumAdapter.view(event, { ...baseAttributes, ...attributes }),
    adapter: {
      link: ({ event, attributes }) =>
        tealiumAdapter.link(event, { ...baseAttributes, ...attributes }),
      view: ({ event, attributes }) =>
        tealiumAdapter.view(event, { ...baseAttributes, ...attributes }),
    },
  };
};
